import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  IconButton,
  TableSortLabel,
  Collapse,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as CsvIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { exportValidationReportToCSV, exportValidationReportToExcel, exportValidationReportToPDF } from '../utils/exportUtils';
import moment from 'moment-timezone';
import ShareReportDialog from '../components/ShareReportDialog';
import { extractApiDataArray } from '../utils/apiData';
import SearchableSelect from '../components/SearchableSelect';

interface ValidationReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  campaignId: string;
  severity: string;
  page: number;
  limit: number;
}

interface ValidationIssue {
  type: 'missing_punch' | 'out_of_sequence' | 'duplicate_entry' | 'orphaned_entry' | 'missing_clock_out';
  message: string;
  expectedEntry: string;
  actualEntry?: string;
  timestamp?: string;
  entryId?: string;
  severity: 'error' | 'warning';
}

interface ValidationResult {
  userId: string;
  employeeId: string;
  employeeName: string;
  date: string;
  issues: ValidationIssue[];
  severity: 'error' | 'warning' | 'clean';
  totalEntries: number;
}

const TimeEntryValidationPage: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<ValidationReportFilters>({
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().subtract(1, 'days').format('YYYY-MM-DD'), // Default to yesterday (exclude today)
    userId: '',
    departmentId: '',
    campaignId: '',
    severity: 'all',
    page: 1,
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'employee' | 'date' | 'severity' | 'issues'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Check if user can share reports (administrator and above)
  const canShareReports = user?.role && ['administrator', 'super_user'].includes(user.role);

  // Fetch users for autocomplete
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await apiClient.users.getAll();
      return response.data.data || [];
    },
  });

  // Fetch departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await apiClient.departments.getAll();
      return response.data.data || [];
    },
  });

  // Fetch campaigns (active + inactive with activity in report range)
  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: async () => {
      const response = await apiClient.campaigns.getAll({
        includeWithActivityFrom: filters.startDate,
        includeWithActivityTo: filters.endDate,
      });
      return response.data.data || [];
    },
  });

  const users = extractApiDataArray(usersData);
  const departments = extractApiDataArray(departmentsData);
  const campaigns = extractApiDataArray(campaignsData);

  // Fetch validation report
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['validation-report', filters],
    queryFn: async () => {
      const params: any = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        page: filters.page,
        limit: filters.limit,
      };

      if (filters.userId) params.userId = filters.userId;
      if (filters.departmentId) params.departmentId = filters.departmentId;
      if (filters.campaignId) params.campaignId = filters.campaignId;
      if (filters.severity && filters.severity !== 'all') params.severity = filters.severity;

      const response = await apiClient.timeEntries.getValidationReport(params);
      return response.data;
    },
    enabled: !!filters.startDate && !!filters.endDate,
  });

  const validationResults = data?.data || [];
  const summary = data?.summary || {
    totalIssues: 0,
    errors: 0,
    warnings: 0,
    affectedEmployees: 0,
    affectedDays: 0,
  };
  const pagination = data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 };

  // Sort results
  const sortedResults = useMemo(() => {
    const sorted = [...validationResults];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'employee':
          comparison = a.employeeName.localeCompare(b.employeeName);
          break;
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'severity':
          const severityOrder: Record<string, number> = { error: 0, warning: 1, clean: 2 };
          comparison = (severityOrder[a.severity] ?? 2) - (severityOrder[b.severity] ?? 2);
          break;
        case 'issues':
          comparison = a.issues.length - b.issues.length;
          break;
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [validationResults, sortBy, sortDir]);

  const handleSort = (column: 'employee' | 'date' | 'severity' | 'issues') => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const fetchAllValidationResultsForExport = async () => {
    const baseParams: any = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      page: 1,
      limit: 1000,
    };
    if (filters.userId) baseParams.userId = filters.userId;
    if (filters.departmentId) baseParams.departmentId = filters.departmentId;
    if (filters.campaignId) baseParams.campaignId = filters.campaignId;
    if (filters.severity && filters.severity !== 'all') baseParams.severity = filters.severity;

    let allResults: any[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getValidationReport({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allResults = allResults.concat(pageData);
      total = pagination?.total ?? allResults.length;

      if (allResults.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allResults;
  };

  const handleExport = async (format: 'csv' | 'excel' | 'pdf') => {
    try {
      const allResults = await fetchAllValidationResultsForExport();
      switch (format) {
        case 'csv':
          exportValidationReportToCSV(allResults, filters);
          break;
        case 'excel':
          exportValidationReportToExcel(allResults, filters);
          break;
        case 'pdf':
          exportValidationReportToPDF(allResults, filters);
          break;
      }
    } catch (error) {
      console.error('Error exporting validation report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExportMenuAnchor(null);
    }
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const toggleRowExpand = (key: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getIssueTypeColor = (type: string) => {
    switch (type) {
      case 'missing_punch':
      case 'missing_clock_out':
        return 'error';
      case 'out_of_sequence':
        return 'error';
      case 'orphaned_entry':
        return 'warning';
      case 'duplicate_entry':
        return 'warning';
      default:
        return 'default';
    }
  };

  const selectedUser = useMemo(() => {
    if (!filters.userId || !users.length) return null;
    return users.find((u: any) => u.id === filters.userId) || null;
  }, [filters.userId, users]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Time Entry Validation Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Identify missing punches and out-of-sequence time entries
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Issues
              </Typography>
              <Typography variant="h4">
                {summary.totalIssues}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Errors
              </Typography>
              <Typography variant="h4" color="error">
                {summary.errors}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Warnings
              </Typography>
              <Typography variant="h4" color="warning.main">
                {summary.warnings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Affected Employees
              </Typography>
              <Typography variant="h4">
                {summary.affectedEmployees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            <Box>
              <Button
                startIcon={<ArrowDropDownIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ mr: 1 }}
              >
                Export
              </Button>
              {canShareReports && (
                <Button
                  startIcon={<EmailIcon />}
                  onClick={handleShare}
                  variant="outlined"
                >
                  Share
                </Button>
              )}
            </Box>
          </Box>

          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleExport('csv')}>
              <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Export CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('excel')}>
              <ListItemIcon><ExcelIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Export Excel</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('pdf')}>
              <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Export PDF</ListItemText>
            </MenuItem>
          </Menu>

          <Collapse in={showFilters}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={users.slice(0, 100)}
                  getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} (${option.employeeId || 'N/A'})`}
                  value={selectedUser}
                  onChange={(_, newValue: any) => {
                    setFilters({ ...filters, userId: newValue?.id || '', page: 1 });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Employee" />
                  )}
                  filterOptions={(options: any[], { inputValue }: { inputValue: string }) => {
                    if (!inputValue) return options.slice(0, 50);
                    const searchLower = inputValue.toLowerCase();
                    return options.filter((option: any) =>
                      `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                      option.employeeId?.toLowerCase().includes(searchLower) ||
                      option.email?.toLowerCase().includes(searchLower)
                    ).slice(0, 100);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Department"
                  value={filters.departmentId}
                  onChange={(value) => setFilters({ ...filters, departmentId: String(value), page: 1 })}
                  options={departments.map((dept: any) => ({
                    value: dept.id,
                    label: dept.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Campaign"
                  value={filters.campaignId}
                  onChange={(value) => setFilters({ ...filters, campaignId: String(value), page: 1 })}
                  options={campaigns.slice(0, 100).map((campaign: any) => ({
                    value: campaign.id,
                    label: campaign.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Severity"
                  value={filters.severity}
                  onChange={(value) => setFilters({ ...filters, severity: String(value), page: 1 })}
                  options={[
                    { value: 'error', label: 'Errors Only' },
                    { value: 'warning', label: 'Warnings Only' },
                  ]}
                />
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Results Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load validation report. Please try again.
          {(process.env.NODE_ENV === 'development' || (window as any).__DEV__) && (
            <Box sx={{ mt: 1 }}>
              <pre style={{ fontSize: '12px', margin: 0 }}>
                {error instanceof Error ? error.message : String(error)}
              </pre>
            </Box>
          )}
          <Button onClick={() => refetch()} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Alert>
      ) : sortedResults.length === 0 ? (
        <Alert severity="info">
          No validation issues found for the selected filters.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'employee'}
                      direction={sortBy === 'employee' ? sortDir : 'asc'}
                      onClick={() => handleSort('employee')}
                    >
                      Employee
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'date'}
                      direction={sortBy === 'date' ? sortDir : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'severity'}
                      direction={sortBy === 'severity' ? sortDir : 'asc'}
                      onClick={() => handleSort('severity')}
                    >
                      Severity
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'issues'}
                      direction={sortBy === 'issues' ? sortDir : 'asc'}
                      onClick={() => handleSort('issues')}
                    >
                      Issues
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Total Entries</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedResults.map((result) => {
                  const rowKey = `${result.userId}_${result.date}`;
                  const isExpanded = expandedRows.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpand(rowKey)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {result.employeeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {result.employeeId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{moment(result.date).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>
                          <Chip
                            label={result.severity}
                            color={getSeverityColor(result.severity) as any}
                            size="small"
                            icon={result.severity === 'error' ? <ErrorIcon /> : <WarningIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={result.issues.length}
                            color={result.issues.length > 0 ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{result.totalEntries}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 2, backgroundColor: 'grey.50' }}>
                            <Box sx={{ pl: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Issues Details:
                              </Typography>
                              {result.issues.map((issue: ValidationIssue, idx: number) => (
                                <Box key={idx} sx={{ mb: 1, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip
                                      label={issue.type}
                                      color={getIssueTypeColor(issue.type) as any}
                                      size="small"
                                    />
                                    <Chip
                                      label={issue.severity}
                                      color={getSeverityColor(issue.severity) as any}
                                      size="small"
                                    />
                                    {issue.timestamp && (
                                      <Typography variant="caption" color="text.secondary">
                                        {moment(issue.timestamp).format('h:mm A')}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography variant="body2">{issue.message}</Typography>
                                  {issue.expectedEntry && (
                                    <Typography variant="caption" color="text.secondary">
                                      Expected: {issue.expectedEntry}
                                    </Typography>
                                  )}
                                  {issue.actualEntry && (
                                    <Typography variant="caption" color="text.secondary">
                                      Actual: {issue.actualEntry}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, newPage) => setFilters({ ...filters, page: newPage })}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Share Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="validation"
          reportTitle="Time Entry Validation Report"
          reportFilters={filters}
        />
      )}
    </Container>
  );
};

export default TimeEntryValidationPage;

